/**
 * SkinSwatch — 디자인 스킨 선택 버튼 (3색 프리뷰 + 라벨).
 *
 * 【역할】
 * - SkinDef.swatch (bg/fg/accent) 를 원형 3조각으로 시각화.
 * - 활성 상태일 때 테두리 강조 + 배경 하이라이트.
 * - AppHeader settingsPanel 의 스킨 선택 섹션에서 사용.
 *
 * 【Props】
 * - def: SkinDef
 * - active: 현재 선택 여부
 * - onClick: 클릭 핸들러
 */
import { theme as antdTheme, Typography } from 'antd'
import type { SkinDef } from '@/design-skins'

interface SkinSwatchProps {
  /** 스킨 정의 — key/label/description/swatch(bg,fg,accent) 포함. */
  def: SkinDef
  /** 현재 선택 여부 — true 일 때 primary border + 배경 하이라이트(약 10% alpha). */
  active: boolean
  /** 스킨 선택 콜백 — 클릭 시 호출. */
  onClick: () => void
}

/**
 * SkinSwatch — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export function SkinSwatch({ def, active, onClick }: SkinSwatchProps) {
  const { token } = antdTheme.useToken()
  const { bg, fg, accent } = def.swatch
  return (
    <div
      role="button"
      aria-label={`skin-${def.key}`}
      aria-pressed={active}
      onClick={onClick}
      title={def.description}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderRadius: token.borderRadius,
        cursor: 'pointer',
        border: `1px solid ${active ? token.colorPrimary : token.colorBorderSecondary}`,
        background: active ? `${token.colorPrimary}1a` : 'transparent',
        transition: 'all 0.15s'
      }}
    >
      {/* 3조각 원형 스와치 */}
      <div
        style={{
          width: 28,
          height: 14,
          borderRadius: 7,
          overflow: 'hidden',
          display: 'flex',
          border: `1px solid ${token.colorBorderSecondary}`
        }}
      >
        <div style={{ flex: 1, background: bg }} />
        <div style={{ flex: 1, background: fg }} />
        <div style={{ flex: 1, background: accent }} />
      </div>
      <Typography.Text style={{ fontSize: token.fontSizeSM }}>{def.label}</Typography.Text>
    </div>
  )
}
